import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';
import { Newspaper, Calendar, X, ExternalLink, Youtube, Music, Instagram, Tag, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import ShareButton from '../components/ShareButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Spotify icon component
const SpotifyIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

// SoundCloud icon component
const SoundCloudIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M1.175 12.225c-.051 0-.094.046-.101.1l-.233 2.154.233 2.105c.007.058.05.098.101.098.05 0 .09-.04.099-.098l.255-2.105-.27-2.154c-.009-.06-.05-.1-.099-.1m-.899.828c-.06 0-.091.037-.104.094L0 14.479l.172 1.282c.013.06.045.094.104.094.058 0 .09-.038.102-.094l.199-1.282-.2-1.332c-.012-.057-.044-.094-.102-.094m1.79-1.143c-.068 0-.12.045-.129.113l-.198 2.455.198 2.401c.009.068.06.113.129.113.068 0 .12-.045.128-.113l.218-2.401-.218-2.455c-.008-.068-.06-.113-.128-.113m.898-.753c-.075 0-.135.053-.143.129l-.178 3.208.178 3.101c.008.075.068.129.143.129.075 0 .135-.053.143-.129l.2-3.101-.2-3.208c-.008-.076-.068-.129-.143-.129m.907-.162c-.09 0-.149.06-.157.143l-.162 3.37.162 3.149c.008.083.067.143.157.143.09 0 .148-.06.156-.143l.18-3.149-.18-3.37c-.008-.083-.066-.143-.156-.143m.907.203c-.097 0-.165.068-.172.15l-.146 3.166.146 3.047c.007.083.075.15.172.15.097 0 .165-.067.172-.15l.162-3.047-.162-3.166c-.007-.082-.075-.15-.172-.15m3.62-1.838c-.12 0-.203.09-.21.195l-.114 4.834.114 3.037c.007.105.09.195.21.195.12 0 .202-.09.21-.195l.127-3.037-.127-4.834c-.008-.105-.09-.195-.21-.195m.9.3c-.128 0-.218.098-.225.21l-.098 4.534.098 3.008c.007.113.097.21.225.21.127 0 .217-.097.225-.21l.108-3.008-.108-4.534c-.008-.112-.098-.21-.225-.21m.9-.257c-.135 0-.232.105-.24.225l-.083 4.79.083 2.994c.008.12.105.226.24.226.135 0 .232-.106.24-.226l.09-2.994-.09-4.79c-.008-.12-.105-.225-.24-.225m.9.197c-.15 0-.255.113-.263.24l-.067 4.593.067 2.978c.008.127.113.24.263.24.15 0 .255-.113.263-.24l.074-2.978-.074-4.593c-.008-.127-.113-.24-.263-.24m5.318 2.004c-.21 0-.39.045-.556.15-.203-2.227-2.07-3.969-4.364-3.969-.556 0-1.094.106-1.582.298-.203.083-.255.158-.255.315v7.8c0 .158.143.3.308.315h6.434c1.178 0 2.137-.96 2.137-2.137 0-1.178-.96-2.137-2.137-2.137"/>
  </svg>
);

const News = () => {
  const { theme, isDarkMode } = useTheme();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [filterTag, setFilterTag] = useState(null);

  useEffect(() => {
    fetchNews();
  }, []);

  // Auto-open article if passed in URL
  useEffect(() => {
    const articleId = searchParams.get('article');
    if (articleId && news.length > 0) {
      const article = news.find(n => n.id === articleId);
      if (article) {
        setSelectedArticle(article);
      }
    }
  }, [searchParams, news]);

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API}/news?limit=50`);
      setNews(res.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Filter news by tag if selected
  const filteredNews = filterTag 
    ? news.filter(item => item.tags?.includes(filterTag))
    : news;

  // Get all unique tags
  const allTags = [...new Set(news.flatMap(item => item.tags || []))];

  // Parse content and convert YouTube/social links to clickable elements
  const renderContent = (content) => {
    if (!content) return null;
    
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Check for YouTube links
      if (line.includes('youtube.com') || line.includes('@')) {
        const urlMatch = line.match(/(https?:\/\/[^\s]+)/);
        if (urlMatch) {
          return (
            <p key={idx} className="mb-2">
              {line.replace(urlMatch[0], '')}
              <a 
                href={urlMatch[0]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 underline"
                style={{ color: theme.primary }}
              >
                {urlMatch[0]} <ExternalLink className="w-3 h-3" />
              </a>
            </p>
          );
        }
      }
      
      // Check for bullet points
      if (line.trim().startsWith('•')) {
        return (
          <li key={idx} className="ml-4 mb-1" style={{ color: 'var(--text-secondary)' }}>
            {line.replace('•', '').trim()}
          </li>
        );
      }
      
      // Emoji headers (🎵, 🎤, etc.)
      if (/^[🎵🎤🌟✍️🎹📱💿🎧🤝📻#]/.test(line.trim())) {
        return (
          <h3 key={idx} className="text-lg font-bold mt-4 mb-2" style={{ color: theme.secondary }}>
            {line}
          </h3>
        );
      }
      
      // Empty lines become paragraph breaks
      if (line.trim() === '') {
        return <br key={idx} />;
      }
      
      return <p key={idx} className="mb-2" style={{ color: 'var(--text-secondary)' }}>{line}</p>;
    });
  };

  const GlassCard = ({ children, className = '', onClick }) => (
    <div 
      className={`backdrop-blur-xl rounded-xl ${className}`}
      style={{ 
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border-color)',
      }}
      onClick={onClick}
    >
      {children}
    </div>
  );

  return (
    <div className="py-4">
      <div className="max-w-5xl mx-auto px-3 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <div>
            <h1 
              className="text-2xl sm:text-3xl font-black uppercase tracking-wider mb-1 flex items-center gap-2"
              style={{ color: theme.primary, textShadow: isDarkMode ? theme.glow : 'none' }}
              data-testid="news-title"
            >
              <Newspaper className="w-6 h-6 sm:w-8 sm:h-8" />
              News
            </h1>
            <p className="text-sm sm:text-base" style={{ color: 'var(--text-muted)' }}>Latest from DJ808 & CRG Productionz</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/submit-article')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 text-white"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit Article
            </Button>
            <ShareButton title="BeatLab52 News" text="Check out the latest news from DJ808 & CRG Productionz!" />
          </div>
        </div>

        {/* Tag Filter */}
        {allTags.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            <Button 
              variant={!filterTag ? "default" : "outline"} 
              size="sm"
              onClick={() => setFilterTag(null)}
              style={!filterTag ? { backgroundColor: theme.primary, color: '#000' } : {}}
            >
              All
            </Button>
            {allTags.slice(0, 8).map(tag => (
              <Button 
                key={tag}
                variant={filterTag === tag ? "default" : "outline"} 
                size="sm"
                onClick={() => setFilterTag(tag)}
                style={filterTag === tag ? { backgroundColor: theme.primary, color: '#000' } : {}}
              >
                <Tag className="w-3 h-3 mr-1" />
                {tag}
              </Button>
            ))}
          </div>
        )}

        {/* News List */}
        <div className="space-y-4 sm:space-y-6">
          {filteredNews.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              data-testid={`news-${item.id}`}
            >
              <GlassCard 
                className="overflow-hidden hover:scale-[1.01] transition-all duration-300 cursor-pointer group"
                onClick={() => navigate(`/news/${item.id}`)}
              >
                <div className="flex flex-col sm:flex-row">
                  {item.image_url && (
                    <div className="sm:w-1/3 aspect-video sm:aspect-square overflow-hidden">
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className={`p-4 sm:p-6 ${item.image_url ? 'sm:w-2/3' : 'w-full'}`}>
                    <div className="flex flex-wrap items-center gap-2 text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.created_at)}
                      {item.category && (
                        <>
                          <span className="hidden sm:inline">•</span>
                          <span 
                            className="uppercase tracking-wider px-2 py-0.5 rounded text-[10px] sm:text-xs"
                            style={{ 
                              backgroundColor: `${theme.primary}20`,
                              color: theme.primary,
                            }}
                          >
                            {item.category}
                          </span>
                        </>
                      )}
                      {item.artist && (
                        <span 
                          className="px-2 py-0.5 rounded text-[10px] sm:text-xs"
                          style={{ 
                            backgroundColor: `${theme.secondary}20`,
                            color: theme.secondary,
                          }}
                        >
                          {item.artist === 'Both' ? 'DJ808 & CRG' : item.artist}
                        </span>
                      )}
                    </div>
                    <h2 
                      className="text-base sm:text-xl font-bold mb-2 sm:mb-3 group-hover:underline line-clamp-2"
                      style={{ color: theme.primary }}
                    >
                      {item.title}
                    </h2>
                    <p className="line-clamp-2 sm:line-clamp-3 text-sm" style={{ color: 'var(--text-secondary)' }}>{item.content}</p>
                    
                    {/* Tags preview */}
                    {item.tags && item.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {item.tags.slice(0, 3).map(tag => (
                          <span 
                            key={tag}
                            className="text-[10px] px-1.5 py-0.5 rounded"
                            style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--text-muted)' }}
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="mt-3 sm:mt-4 text-sm"
                      style={{ color: theme.secondary }}
                    >
                      Read More →
                    </Button>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        {news.length === 0 && !loading && (
          <GlassCard className="p-16 text-center">
            <Newspaper className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p className="text-lg" style={{ color: 'var(--text-muted)' }}>No news yet</p>
            <p className="text-sm mt-2" style={{ color: 'var(--text-muted)' }}>Check back soon for updates!</p>
          </GlassCard>
        )}

        {/* Article Modal */}
        <Dialog open={!!selectedArticle} onOpenChange={() => setSelectedArticle(null)}>
          <DialogContent 
            className="max-w-3xl max-h-[90vh]"
            style={{ 
              backgroundColor: isDarkMode ? 'rgba(0,0,0,0.98)' : 'rgba(255,255,255,0.98)',
              borderColor: 'var(--border-color)',
            }}
          >
            <DialogHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
                  <Calendar className="w-3 h-3" />
                  {selectedArticle && formatDate(selectedArticle.created_at)}
                  {selectedArticle?.category && (
                    <>
                      <span>•</span>
                      <span 
                        className="uppercase tracking-wider px-2 py-0.5 rounded"
                        style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}
                      >
                        {selectedArticle.category}
                      </span>
                    </>
                  )}
                  {selectedArticle?.artist && (
                    <span 
                      className="px-2 py-0.5 rounded"
                      style={{ backgroundColor: `${theme.secondary}20`, color: theme.secondary }}
                    >
                      {selectedArticle.artist === 'Both' ? 'DJ808 & CRG' : selectedArticle.artist}
                    </span>
                  )}
                </div>
                <ShareButton 
                  title={selectedArticle?.title}
                  text={`${selectedArticle?.title} - BeatLab52 News`}
                />
              </div>
              <DialogTitle 
                className="text-2xl font-bold mt-2"
                style={{ color: theme.primary }}
              >
                {selectedArticle?.title}
              </DialogTitle>
            </DialogHeader>
            
            {selectedArticle?.image_url && (
              <div className="aspect-video rounded-lg overflow-hidden mb-4">
                <img 
                  src={selectedArticle.image_url} 
                  alt={selectedArticle.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Spotify Embed */}
            {selectedArticle?.spotify_embed && (
              <div className="mb-4 rounded-lg overflow-hidden">
                <iframe
                  src={selectedArticle.spotify_embed}
                  width="100%"
                  height="352"
                  frameBorder="0"
                  allowFullScreen=""
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  loading="lazy"
                  style={{ borderRadius: '12px' }}
                  title="Spotify Player"
                />
              </div>
            )}
            
            <ScrollArea className="max-h-[40vh]">
              <div className="pr-4">
                {renderContent(selectedArticle?.content)}
              </div>
            </ScrollArea>

            {/* Tags */}
            {selectedArticle?.tags && selectedArticle.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {selectedArticle.tags.map(tag => (
                  <span 
                    key={tag}
                    className="text-xs px-2 py-1 rounded cursor-pointer hover:opacity-80"
                    style={{ backgroundColor: `${theme.primary}20`, color: theme.primary }}
                    onClick={(e) => { e.stopPropagation(); setSelectedArticle(null); setFilterTag(tag); }}
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Social Links */}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t" style={{ borderColor: 'var(--border-color)' }}>
              {selectedArticle?.instagram_url && (
                <a href={selectedArticle.instagram_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Instagram className="w-4 h-4" />
                    Instagram
                  </Button>
                </a>
              )}
              {selectedArticle?.soundcloud_url && (
                <a href={selectedArticle.soundcloud_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <SoundCloudIcon />
                    SoundCloud
                  </Button>
                </a>
              )}
              {selectedArticle?.apple_music_url && (
                <a href={selectedArticle.apple_music_url} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Music className="w-4 h-4" />
                    Apple Music
                  </Button>
                </a>
              )}
              <a href="https://open.spotify.com/artist/3NMtCG5XHNiW6GdcdWtJdr" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-1">
                  <SpotifyIcon />
                  DJ808 Spotify
                </Button>
              </a>
              <a href="https://open.spotify.com/artist/6KqiuYSW5MyI0obuB7vBDI" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-1">
                  <SpotifyIcon />
                  CRG Spotify
                </Button>
              </a>
              <a href="https://www.youtube.com/@BEATLAB52MR808" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-1">
                  <Youtube className="w-4 h-4" />
                  DJ808 YouTube
                </Button>
              </a>
              <a href="https://www.youtube.com/@C.R.GProductionz" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="gap-1">
                  <Youtube className="w-4 h-4" />
                  CRG YouTube
                </Button>
              </a>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default News;
